import { Injectable } from "@angular/core";
import { Params, Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
	AccountInfo,
	AuthenticationResult,
	EndSessionRequest,
	EventMessage,
	EventType,
	RedirectRequest,
	SsoSilentRequest,
} from "@azure/msal-browser";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment.ipt.local";
import {
	AccessNoticeType,
	ACCOUNT_SWITCH_UNAVAILABLE_MESSAGE,
	AUTH_ERROR_MESSAGE,
	AZURE_ACCOUNT_NOT_FOUND_MESSAGE,
	MULTIPLE_ACCOUNTS_MESSAGE,
	NO_ACTIVE_SESSION_MESSAGE,
	PORTAL_SESSION_REQUIRED_MESSAGE,
	SESSION_EXPIRED_MESSAGE,
} from "../data/constants";
import { UserSessionStateService } from "../services/user-session-state.service";
import { AuthStateStorageService } from "./auth-state-storage.service";
import { AuthUrlService } from "./auth-url.service";
import { AzureAccountService } from "./azure-account.service";
import { AzureAuthErrorService } from "./azure-auth-error.service";
import { AzureCrossTabService } from "./azure-cross-tab.service";
import { TokenRecoveryLockService } from "./token-recovery-lock.service";

@Injectable({
	providedIn: "root",
})
export class AzureSsoService {
	private readonly defaultScopes = ["user.read"];
	private authenticationPromise: Promise<boolean> | null = null;
	private tokenRecoveryPromise: Promise<AuthenticationResult | null> | null =
		null;
	private initializationPromise: Promise<void> | null = null;
	private initializationError: string | null = null;
	readonly sessionChanged$ = this.crossTabService.changed$;

	constructor(
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private router: Router,
		private userSessionStateService: UserSessionStateService,
		private accountService: AzureAccountService,
		private stateStorage: AuthStateStorageService,
		private crossTabService: AzureCrossTabService,
		private authErrorService: AzureAuthErrorService,
		private recoveryLock: TokenRecoveryLockService,
		private authUrl: AuthUrlService
	) {
		this.watchMsalEvents();
	}

	initialize(): Promise<void> {
		if (!this.initializationPromise) {
			this.initializationPromise = this.initializeMsal();
		}

		return this.initializationPromise;
	}

	ensureAuthenticated(returnUrl = window.location.pathname): Promise<boolean> {
		if (!this.authenticationPromise) {
			this.authenticationPromise = this.initialize()
				.then(() => this.authenticate(returnUrl))
				.finally(() => {
					this.authenticationPromise = null;
				});
		}

		return this.authenticationPromise;
	}

	async acquireAccessToken(
		scopes: string[] = this.defaultScopes
	): Promise<string | null> {
		if (this.crossTabService.hasChanged) {
			return null;
		}

		const account = this.getPreferredAccount(this.getStoredLoginHint());

		if (!account) {
			this.failAuthentication(AUTH_ERROR_MESSAGE);
			return null;
		}

		this.setActiveAccount(account);

		try {
			const result = await this.msalService.instance.acquireTokenSilent({
				account,
				scopes,
			});
			return result.accessToken;
		} catch (error) {
			if (!this.authErrorService.isRecoverable(error)) {
				throw error;
			}

			const result = await this.recoverSession(account, scopes);
			return result?.accessToken || null;
		}
	}

	getLoginHintFromUrl(): string | null {
		return this.accountService.normalizeLoginHint(
			this.authUrl.getLoginHint()
		);
	}

	getLoginHintFromRouteParams(params: Params): string | null {
		return this.accountService.normalizeLoginHint(
			params["loginHint"] || params["login_hint"] || null
		);
	}

	getPreferredAccount(loginHint: string | null) {
		return this.accountService.getPreferred(loginHint);
	}

	getCurrentAccount() {
		return this.accountService.getCurrent();
	}

	getAccessNotice() {
		return this.stateStorage.getAccessNotice();
	}

	clearAccessNotice(): void {
		this.stateStorage.clearAccessNotice();
	}

	setActiveAccount(account: any): void {
		this.accountService.setActive(account);
	}

	clearAuthQueryParamsFromUrl(): void {
		this.authUrl.clearAuthParams();
	}

	async logoutFromApp(redirectUrl = environment.urlPortalOrigin): Promise<void> {
		const logoutRequest: EndSessionRequest = {
			account: this.msalService.instance.getActiveAccount() || undefined,
			onRedirectNavigate: () => false,
		};

		this.userSessionStateService.clear();
		this.clearAppStorage();
		this.stateStorage.clearPendingRedirect();
		this.stateStorage.markLocalLogout();
		this.setActiveAccount(null);

		try {
			await this.clearLocalMsalAccounts();

			// Fallback for MSAL versions that do not expose removeAccount.
			if (!this.canRemoveLocalMsalAccounts()) {
				await this.msalService.instance.logoutRedirect(logoutRequest);
			}
		} finally {
			this.crossTabService.publish("session-cleared", null);
			window.location.href = redirectUrl;
		}
	}

	// Last-resort recovery for a stuck user: wipe the whole origin storage
	// (covers dead/orphaned MSAL cache and expired tokens that removeAccount
	// can miss) and hard-navigate to the portal to re-authenticate cleanly.
	// Does not rely on MSAL internal state, which may itself be corrupt. The
	// portal re-arrives with a loginHint, so the working account rebuilds itself.
	hardResetSession(redirectUrl = environment.urlPortalOrigin): void {
		try {
			localStorage.clear();
			sessionStorage.clear();
		} finally {
			window.location.href = redirectUrl;
		}
	}

	isSessionChangedInAnotherTab(): boolean {
		return this.crossTabService.hasChanged;
	}

	reloadForSessionChange(): void {
		window.location.reload();
	}

	private async authenticate(returnUrl: string): Promise<boolean> {
		if (this.initializationError) {
			return this.failAuthentication(this.initializationError);
		}

		const urlLoginHint = this.getLoginHintFromUrl();
		const activeAccount = this.msalService.instance.getActiveAccount();

		// Portal arrival with a different identity: verify the requested session
		// silently BEFORE dropping the current one (transactional switch).
		if (
			urlLoginHint &&
			activeAccount &&
			!this.accountService.matchesLoginHint(activeAccount, urlLoginHint)
		) {
			return this.switchToLoginHint(urlLoginHint);
		}

		if (urlLoginHint) {
			this.stateStorage.clearFailedRedirect();
		} else if (this.stateStorage.hasFailedRedirect()) {
			return this.failAuthentication(SESSION_EXPIRED_MESSAGE);
		}

		const loginHint = urlLoginHint || this.getStoredLoginHint();
		const pendingRedirect = this.stateStorage.getPendingRedirect();
		const account = this.getPreferredAccount(loginHint);

		if (
			!loginHint &&
			!account &&
			this.accountService.hasAmbiguousAccounts()
		) {
			return this.failAuthentication(MULTIPLE_ACCOUNTS_MESSAGE);
		}

		if (account) {
			this.setActiveAccount(account);

			try {
				await this.msalService.instance.acquireTokenSilent({
					account,
					scopes: this.defaultScopes,
				});
				this.completeAuthentication(account);
				return true;
			} catch (error) {
				if (!this.authErrorService.isRecoverable(error)) {
					return this.failAuthentication(AUTH_ERROR_MESSAGE);
				}

				const recovered = await this.recoverSession(
					account,
					this.defaultScopes,
					returnUrl
				);
				return Boolean(recovered);
			}
		}

		if (pendingRedirect) {
			return this.failAuthentication(SESSION_EXPIRED_MESSAGE);
		}

		if (!loginHint) {
			return this.failAuthentication(NO_ACTIVE_SESSION_MESSAGE);
		}

		try {
			const result = await this.msalService.instance.ssoSilent(
				this.createSilentRequest(loginHint)
			);
			if (
				!result.account ||
				!this.accountService.matchesLoginHint(
					result.account,
					loginHint
				)
			) {
				await this.clearLocalMsalAccounts([result.account]);
				return this.failAuthentication(
					ACCOUNT_SWITCH_UNAVAILABLE_MESSAGE
				);
			}

			this.completeAuthentication(result.account);
			return true;
		} catch (error) {
			return this.redirectSilentlyOrFail(
				error,
				loginHint,
				returnUrl,
				SESSION_EXPIRED_MESSAGE
			);
		}
	}

	// Access to Core is only valid through the portal. When a different email
	// arrives, the requested account is switched in only if it already holds a
	// live portal session; otherwise the current session is left untouched.
	private async switchToLoginHint(loginHint: string): Promise<boolean> {
		let result: AuthenticationResult;

		try {
			result = await this.msalService.instance.ssoSilent(
				this.createSilentRequest(loginHint)
			);
		} catch (error) {
			// Requested email has no active portal session (or does not exist):
			// keep the current session intact and guide the user to the portal.
			const message = this.authErrorService.isAccountNotFound(error)
				? AZURE_ACCOUNT_NOT_FOUND_MESSAGE
				: PORTAL_SESSION_REQUIRED_MESSAGE;
			return this.noticeAndReturnHome(message, "session");
		}

		if (
			!result.account ||
			!this.accountService.matchesLoginHint(result.account, loginHint)
		) {
			return this.noticeAndReturnHome(
				PORTAL_SESSION_REQUIRED_MESSAGE,
				"session"
			);
		}

		// Verified: commit the switch, dropping the previous user's state.
		this.clearAppSessionState();
		await this.clearLocalMsalAccountsExcept(loginHint);
		this.completeAuthentication(result.account);
		return true;
	}

	private async initializeMsal(): Promise<void> {
		try {
			await this.msalService.instance.initialize();
			const result =
				await this.msalService.instance.handleRedirectPromise();

			if (result?.account) {
				this.completeAuthentication(result.account);
				return;
			}

			// Not returning from a redirect this load: any leftover pendingRedirect
			// is from an interrupted/orphaned attempt. Consume it so it cannot block
			// a fresh recovery for the next 5 min (one redirect per round-trip).
			if (!result && this.stateStorage.getPendingRedirect()) {
				this.stateStorage.clearPendingRedirect();
			}

			const urlLoginHint = this.getLoginHintFromUrl();
			if (urlLoginHint && this.stateStorage.consumeLocalLogout()) {
				await this.clearLocalMsalAccountsExcept(urlLoginHint);
			}

			const activeAccount =
				this.msalService.instance.getActiveAccount();
			if (activeAccount) {
				return;
			}

			const accounts = this.msalService.instance.getAllAccounts();
			if (accounts.length === 1) {
				if (
					!urlLoginHint ||
					this.accountService.matchesLoginHint(accounts[0], urlLoginHint)
				) {
					this.setActiveAccount(accounts[0]);
				}
			}
		} catch (error) {
			// Only a missing/invalid account is fatal. Any transient error (stale
			// interaction state, iframe timeout, expired token) must NOT brick the
			// app to home: leave it to authenticate() to re-drive ssoSilent/redirect.
			if (this.authErrorService.isAccountNotFound(error)) {
				this.initializationError = AZURE_ACCOUNT_NOT_FOUND_MESSAGE;
				this.recordAuthenticationFailure(
					this.initializationError,
					"authentication"
				);
				this.clearAuthQueryParamsFromUrl();
			}
		}
	}

	private async recoverSession(
		account: AccountInfo,
		scopes: string[],
		returnUrl = this.authUrl.currentRelative()
	): Promise<AuthenticationResult | null> {
		if (!this.tokenRecoveryPromise) {
			this.tokenRecoveryPromise = this.recoverSessionAcrossTabs(
				account,
				scopes,
				returnUrl
			).finally(() => {
				this.tokenRecoveryPromise = null;
			});
		}

		return this.tokenRecoveryPromise;
	}

	private async recoverSessionAcrossTabs(
		account: AccountInfo,
		scopes: string[],
		returnUrl: string
	): Promise<AuthenticationResult | null> {
		if (this.acquireTokenRecoveryLock()) {
			return this.runOwnedTokenRecovery(account, scopes, returnUrl);
		}

		await this.waitForTokenRecovery();

		try {
			const result = await this.msalService.instance.acquireTokenSilent({
				account,
				scopes,
			});
			this.completeAuthentication(result.account);
			return result;
		} catch (error) {
			if (!this.authErrorService.isRecoverable(error)) {
				throw error;
			}
		}

		if (this.acquireTokenRecoveryLock()) {
			return this.runOwnedTokenRecovery(account, scopes, returnUrl);
		}

		this.failAuthentication(SESSION_EXPIRED_MESSAGE);
		return null;
	}

	private async runOwnedTokenRecovery(
		account: AccountInfo,
		scopes: string[],
		returnUrl: string
	): Promise<AuthenticationResult | null> {
		try {
			return await this.tryRecoverSession(account, scopes, returnUrl);
		} finally {
			if (!this.stateStorage.getPendingRedirect()) {
				this.releaseTokenRecoveryLock();
			}
		}
	}

	private async tryRecoverSession(
		account: AccountInfo,
		scopes: string[],
		returnUrl: string
	): Promise<AuthenticationResult | null> {
		// The loginHint from the portal URL is authoritative; fall back to the
		// cached account's hint. Resolving by loginHint (not by the stale cached
		// account) matches the clean path that already works from the portal.
		const urlLoginHint = this.getLoginHintFromUrl();
		const loginHint =
			urlLoginHint || this.accountService.getLoginHint(account);

		const request: SsoSilentRequest = { scopes };
		if (loginHint) {
			request.loginHint = loginHint;
		} else {
			request.account = account;
		}

		try {
			const result = await this.msalService.instance.ssoSilent(request);
			if (
				loginHint &&
				(!result.account ||
					!this.accountService.matchesLoginHint(
						result.account,
						loginHint
					))
			) {
				await this.clearLocalMsalAccounts(
					result.account ? [result.account] : []
				);
				this.failAuthentication(ACCOUNT_SWITCH_UNAVAILABLE_MESSAGE);
				return null;
			}

			this.completeAuthentication(result.account);
			return result;
		} catch (error) {
			await this.redirectSilentlyOrFail(
				error,
				loginHint,
				returnUrl,
				SESSION_EXPIRED_MESSAGE,
				urlLoginHint ? undefined : account,
				scopes
			);
			return null;
		}
	}

	private async redirectSilentlyOrFail(
		error: unknown,
		loginHint: string | null,
		returnUrl: string,
		message: string,
		account?: AccountInfo,
		scopes = this.defaultScopes
	): Promise<boolean> {
		if (!this.authErrorService.isRecoverable(error)) {
			return this.failAuthentication(message);
		}

		if (this.stateStorage.getPendingRedirect()) {
			return this.failAuthentication(message);
		}

		this.stateStorage.setPendingRedirect({
			createdAt: Date.now(),
			loginHint,
			returnUrl,
		});

		const request: RedirectRequest = {
			account,
			prompt: "none",
			redirectStartPage: this.authUrl.toAbsolute(returnUrl),
			scopes,
		};

		if (!account && loginHint) {
			request.loginHint = loginHint;
		}

		try {
			await this.msalService.instance.acquireTokenRedirect(request);
		} catch {
			return this.failAuthentication(message);
		}

		return false;
	}

	private completeAuthentication(account: AccountInfo): void {
		this.setActiveAccount(account);
		this.stateStorage.clearPendingRedirect();
		this.stateStorage.clearFailedRedirect();
		this.releaseTokenRecoveryLock(true);
		this.clearAccessNotice();
		this.clearAuthQueryParamsFromUrl();
	}

	private failAuthentication(message: string): false {
		this.recordAuthenticationFailure(
			message,
			this.getAuthenticationNoticeType(message)
		);

		if (window.location.pathname !== "/" || this.router.url !== "/") {
			void this.router.navigateByUrl("/");
		}

		return false;
	}

	// Softer failure than failAuthentication: shows a notice and returns home
	// WITHOUT marking a failed redirect, so an existing valid session is not
	// blocked (used when a requested account switch cannot be verified).
	private noticeAndReturnHome(
		message: string,
		type: AccessNoticeType
	): false {
		this.stateStorage.clearPendingRedirect();
		this.releaseTokenRecoveryLock();
		this.stateStorage.setAccessNotice({ type, message });
		this.clearAuthQueryParamsFromUrl();

		if (window.location.pathname !== "/" || this.router.url !== "/") {
			void this.router.navigateByUrl("/");
		}

		return false;
	}

	private getAuthenticationNoticeType(message: string): AccessNoticeType {
		if (
			message === SESSION_EXPIRED_MESSAGE ||
			message === NO_ACTIVE_SESSION_MESSAGE ||
			message === PORTAL_SESSION_REQUIRED_MESSAGE
		) {
			return "session";
		}

		if (message === MULTIPLE_ACCOUNTS_MESSAGE) {
			return "accounts";
		}

		return "authentication";
	}

	private recordAuthenticationFailure(
		message: string,
		type: AccessNoticeType = "authentication"
	): void {
		this.stateStorage.clearPendingRedirect();
		this.releaseTokenRecoveryLock();
		this.stateStorage.setFailedRedirect();
		this.stateStorage.setAccessNotice({ type, message });
	}

	private watchMsalEvents(): void {
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(message: EventMessage) =>
						message.eventType === EventType.LOGIN_SUCCESS ||
						message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
				)
			)
			.subscribe((message: EventMessage) => {
				const result = message.payload as AuthenticationResult | null;
				if (result?.account) {
					this.completeAuthentication(result.account);
				}
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(message: EventMessage) =>
						message.eventType === EventType.LOGIN_FAILURE ||
						message.eventType === EventType.ACQUIRE_TOKEN_FAILURE
				)
			)
			.subscribe(() => {
				if (this.stateStorage.getPendingRedirect()) {
					this.failAuthentication(SESSION_EXPIRED_MESSAGE);
				}
			});
	}

	private createSilentRequest(loginHint: string | null): SsoSilentRequest {
		const request: SsoSilentRequest = {
			scopes: this.defaultScopes,
		};

		if (loginHint) {
			request.loginHint = loginHint;
		}

		return request;
	}

	private getStoredLoginHint(): string | null {
		return (
			this.getLoginHintFromUrl() ||
			this.stateStorage.getPendingRedirect()?.loginHint ||
			this.accountService.getLoginHint(
				this.msalService.instance.getActiveAccount()
			)
		);
	}

	private acquireTokenRecoveryLock(): boolean {
		return this.recoveryLock.acquire();
	}

	private releaseTokenRecoveryLock(force = false): void {
		this.recoveryLock.release(force);
	}

	private async waitForTokenRecovery(): Promise<void> {
		await this.recoveryLock.wait();
	}

	// Clears the functional app state of the previous user so a switched-in
	// account never inherits the prior session's data.
	private clearAppSessionState(): void {
		this.userSessionStateService.clear();
		sessionStorage.removeItem("app_search_flow_state");
		sessionStorage.removeItem("app_active_flow_id");
		localStorage.removeItem("user-data");
		localStorage.removeItem("userRoleLowFlow");
	}

	private canRemoveLocalMsalAccounts(): boolean {
		const instance = this.msalService.instance as {
			removeAccount?: (account: AccountInfo) => Promise<void>;
		};

		return typeof instance.removeAccount === "function";
	}

	private async clearLocalMsalAccounts(accounts?: AccountInfo[]): Promise<void> {
		const instance = this.msalService.instance as {
			removeAccount?: (account: AccountInfo) => Promise<void>;
		};

		if (!this.canRemoveLocalMsalAccounts()) {
			return;
		}

		const accountsToRemove =
			accounts ?? this.msalService.instance.getAllAccounts();

		for (const account of accountsToRemove) {
			await instance.removeAccount(account);
		}
	}

	private async clearLocalMsalAccountsExcept(
		loginHint: string
	): Promise<void> {
		const activeAccount = this.msalService.instance.getActiveAccount();
		const accounts = [
			...(activeAccount ? [activeAccount] : []),
			...this.msalService.instance.getAllAccounts(),
		];
		const accountsToRemove = accounts.filter(
			(account, index, self) =>
				self.findIndex(
					(item) => item.homeAccountId === account.homeAccountId
				) === index &&
				!this.accountService.matchesLoginHint(account, loginHint)
		);

		await this.clearLocalMsalAccounts(accountsToRemove);
		if (
			activeAccount &&
			!this.accountService.matchesLoginHint(activeAccount, loginHint)
		) {
			this.setActiveAccount(null);
		}
	}

	private clearAppStorage(): void {
		this.stateStorage.clearAccessNotice();
		sessionStorage.removeItem("app_search_flow_state");
		sessionStorage.removeItem("app_active_flow_id");
		this.stateStorage.clearFailedRedirect();
		this.releaseTokenRecoveryLock();
		localStorage.removeItem("user-data");
	}
}
